/**
 * The autosave's one decision: save now (on the backoff), keep this state on
 * the device, or wait (outage lane, 2026-09-29).
 *
 * During the 2026-09-29 backend deploy the autosave re-sent the whole project
 * into a 502 on its backoff for ten minutes. While QuoteMaker's server is not
 * answering, nothing is sent: the save stops before the network and keeps the
 * envelope on this device (use-project-persistence). Each distinct editor
 * state is kept once; after that the autosave waits for the server, and the
 * reconnect loop (network-resilience) brings it back.
 */

export const KEEP_ON_DEVICE_DEBOUNCE_MS = 2000;

export interface AutosavePlanInput {
    hasUnsavedChanges: boolean;
    isSavingProject: boolean;
    /** A conflict or manual-retry latch owns this envelope. */
    blocked: boolean;
    backendReachable: boolean;
    currentFingerprint: string | null;
    /** The state last kept on this device while the server was not answering. */
    keptFingerprint: string | null;
    retryDelayMs: number;
}

export type AutosavePlan =
    | { action: 'none' }
    | { action: 'save'; delayMs: number; countsAsAttempt: boolean };

export function planAutosave({
    hasUnsavedChanges,
    isSavingProject,
    blocked,
    backendReachable,
    currentFingerprint,
    keptFingerprint,
    retryDelayMs,
}: AutosavePlanInput): AutosavePlan {
    if (!hasUnsavedChanges || isSavingProject || blocked) return { action: 'none' };
    if (!backendReachable) {
        if (currentFingerprint && keptFingerprint && currentFingerprint === keptFingerprint) {
            return { action: 'none' };
        }
        // The save runs but sends nothing: it keeps this state on the device.
        // No network try, so it does not grow the backoff.
        return { action: 'save', delayMs: KEEP_ON_DEVICE_DEBOUNCE_MS, countsAsAttempt: false };
    }
    return { action: 'save', delayMs: retryDelayMs, countsAsAttempt: true };
}
